import { Card, Table, Tag, Spin, Alert, Button, Row, Col, Statistic } from "antd";
import { useState, useEffect, useCallback } from "react";
import { IoRefresh, IoCheckmarkCircle, IoCloseCircle, IoStatsChart } from "react-icons/io5";
import { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import { WHATSAPP_API } from "../../../utils/apiRequest";
import dayjs from "dayjs";

interface Message {
  id: number;
  to_number: string;
  message_type: string;
  content: string;
  status: string;
  error_message: string | null;
  sent_at: string;
}

interface MessageStatsProps {
  sessionId: string;
}

const MessageStats = ({ sessionId }: MessageStatsProps) => {
  const user = useSelector((state: any) => state?.User?.user);
  const office = user?.office;

  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);

  const fetchMessages = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await WHATSAPP_API.call(`/messages/${sessionId}?limit=100`);
      setMessages(data.messages || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      fetchMessages();
    }
  }, [sessionId, fetchMessages]);

  const messageColumns: ColumnsType<Message> = [
    {
      title: 'Date & Time',
      dataIndex: 'sent_at',
      key: 'sent_at',
      width: 180,
      render: (date: string) => dayjs(date).format('MMM DD, YYYY HH:mm:ss'),
      sorter: (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Phone Number',
      dataIndex: 'to_number',
      key: 'to_number',
      width: 150,
    },
    {
      title: 'Message',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
      render: (content: string) => (
        <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {content}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'message_type',
      key: 'message_type',
      width: 80,
      align: 'center',
      render: (type: string) => {
        const colors: any = { text: 'blue', pdf: 'orange', image: 'purple' };
        return <Tag color={colors[type] || 'default'}>{type.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      align: 'center',
      render: (status: string) => {
        if (status === 'sent') {
          return (
            <Tag icon={<IoCheckmarkCircle />} color="success">
              Sent
            </Tag>
          );
        } else {
          return (
            <Tag icon={<IoCloseCircle />} color="error">
              Failed
            </Tag>
          );
        }
      },
      filters: [
        { text: 'Sent', value: 'sent' },
        { text: 'Failed', value: 'failed' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Error Reason',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      render: (error: string | null, record: Message) => {
        if (record.status === 'failed') {
          return error ? (
            <Tag color="red">{error}</Tag>
          ) : (
            <Tag color="orange">No error details</Tag>
          );
        }
        return '-';
      },
    },
  ];

  const sentCount = messages.filter(m => m.status === 'sent').length;
  const failedCount = messages.filter(m => m.status === 'failed').length;
  const totalMessages = messages.length;
  const successRate = totalMessages > 0 ? ((sentCount / totalMessages) * 100).toFixed(1) : '0';

  return (
    <div>
      {/* Statistics Summary */}
      <Card className="mb-3">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="user-heading mb-0">
            <IoStatsChart className="me-2" />
            Message Statistics
          </h5>
          <Button
            icon={<IoRefresh />}
            onClick={fetchMessages}
            loading={loading}
            size="small"
          >
            Refresh
          </Button>
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Total Messages"
              value={totalMessages}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Sent Successfully"
              value={sentCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<IoCheckmarkCircle />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Failed"
              value={failedCount}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<IoCloseCircle />}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Success Rate"
              value={successRate}
              suffix="%"
              valueStyle={{
                color: parseFloat(successRate) >= 80 ? '#52c41a' : parseFloat(successRate) >= 50 ? '#faad14' : '#ff4d4f'
              }}
            />
          </Col>
        </Row>
      </Card>

      {/* Messages Table */}
      <Card>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h5 className="user-heading mb-0">
            All Messages
          </h5>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spin size="large" />
          </div>
        ) : messages.length > 0 ? (
          <Table
            columns={messageColumns}
            dataSource={messages}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} messages`
            }}
            size="small"
            scroll={{ x: 1200 }}
          />
        ) : (
          <Alert
            message="No messages found"
            description="No messages have been sent from this session yet."
            type="info"
          />
        )}
      </Card>
    </div>
  );
};

export default MessageStats;
